// The hidden view toggles (ui-map-interface.md §9, R456): five keys that
// hide or show one map layer each, plus the two hex-grid layers nothing
// else can reach.
//
// The state is session-only and is NOT part of GameSettings. A stored
// "terrain off" would greet the next reload with a blank sea floor and no
// on-screen control to undo it, because the KEYS panel hides these rows on
// purpose. A reload is therefore the way back to the shipped picture.
//
// This module is pure: it holds no layer and no DOM. `fast/main.ts` owns one
// LayerView and writes the booleans onto the layers in the frame loop,
// because the territory and terrain layers are rebuilt on every hello.

import { keymap as S } from '../gen/strings';
import type { MapKeyId } from '../gen/presentation';

export interface LayerView {
  sovFills: boolean;
  sovBorders: boolean;
  terrain: boolean;
  hexFills: boolean;
  hexBorders: boolean;
}

/** The shipped picture: the three drawn layers on, the two hex layers off. */
export function defaultLayerView(): LayerView {
  return { sovFills: true, sovBorders: true, terrain: true, hexFills: false, hexBorders: false };
}

export interface LayerToggle {
  /** The binding of `KEYS.map` the handler answers with this row (R853). */
  id: MapKeyId;
  field: keyof LayerView;
  does: string;
}

export const LAYER_TOGGLES: LayerToggle[] = [
  { id: 'sov_fills', field: 'sovFills', does: S.does_sov_fills },
  { id: 'sov_borders', field: 'sovBorders', does: S.does_sov_borders },
  { id: 'terrain', field: 'terrain', does: S.does_terrain },
  { id: 'hex_fills', field: 'hexFills', does: S.does_hex_fills },
  { id: 'hex_borders', field: 'hexBorders', does: S.does_hex_borders },
];

const LAYER_FIELD_BY_ID = new Map<MapKeyId, keyof LayerView>(
  LAYER_TOGGLES.map((t) => [t.id, t.field]),
);

/**
 * The field a matched binding flips, or null where the binding toggles no
 * layer. The input matches a press once with `matchKey`; the five rows are
 * `altgr` bindings, so it takes `[` and `]` under the AltGr chord as well
 * (R456, R853, R2682).
 */
export function layerToggleForId(id: MapKeyId): keyof LayerView | null {
  return LAYER_FIELD_BY_ID.get(id) ?? null;
}
